import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useQueryClient } from "@tanstack/react-query";
import { api, setOnTokenRefreshed } from "@/api/api";
import config from "@/config";
import type { User } from "@/types";
import { AuthContext } from "@/context/auth-context";

interface DecodedToken {
  sub: string;
  iat: number;
  exp: number;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("authToken"),
  );
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Silent refresh: refresh token without user interaction
  const silentRefresh = useCallback(async (): Promise<boolean> => {
    const accessToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken || !accessToken) return false;

    try {
      const response = await axios.post<{
        accessToken: string;
        refreshToken: string;
      }>(
        `${config.api.baseURL}/users/refresh-token`,
        { refreshToken },
        {
          timeout: config.api.timeout,
          withCredentials: true,
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      const newToken = response.data.accessToken;
      const newRefreshToken = response.data.refreshToken;

      localStorage.setItem("authToken", newToken);
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }
      api.defaults.headers.Authorization = `Bearer ${newToken}`;
      setToken(newToken);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Fetch user profile using current token
  const fetchUser = useCallback(async (currentToken: string) => {
    try {
      const decodedToken: DecodedToken = jwtDecode(currentToken);
      const userId = decodedToken.sub;

      api.defaults.headers.Authorization = `Bearer ${currentToken}`;
      const response = await api.get<User>(`/users/${userId}`);
      setUser(response.data);
    } catch (error: unknown) {
      // Only clear session on 401 (token truly invalid/expired and refresh failed)
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        console.error("Failed to fetch user:", error);
        setToken(null);
        setUser(null);
        localStorage.removeItem("authToken");
      }
      // Non-401 errors: silently ignore (interceptor already handled refresh)
    }
    setIsLoading(false);
  }, []);

  // When token changes: sync to localStorage + fetch user
  useEffect(() => {
    if (token) {
      localStorage.setItem("authToken", token);
      setIsLoading(true);
      fetchUser(token);
    } else {
      localStorage.removeItem("authToken");
    }
  }, [token, fetchUser]);

  // On mount: try silent refresh if no access token but has refresh token
  useEffect(() => {
    const accessToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!accessToken && refreshToken) {
      silentRefresh(); // Sets token → triggers above effect → fetchUser
    } else if (!accessToken && !refreshToken) {
      setIsLoading(false);
    }
    // If accessToken exists, the [token] effect handles it
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register callback: interceptor notifies us when token is refreshed
  useEffect(() => {
    setOnTokenRefreshed((newToken: string) => {
      setToken(newToken);
    });
  }, []);

  // Proactive refresh: refresh 1 minute before expiry
  useEffect(() => {
    if (!token) return;

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      const timeUntilRefresh = expiresAt - now - 60_000; // 1 min before expiry

      if (timeUntilRefresh <= 0) {
        // Token already expired or about to expire — refresh now
        silentRefresh();
        return;
      }

      const timer = setTimeout(() => {
        silentRefresh();
      }, timeUntilRefresh);

      return () => clearTimeout(timer);
    } catch {
      // Invalid token — don't set timer
    }
  }, [token, silentRefresh]);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    delete (api.defaults.headers as Record<string, unknown>)["Authorization"];
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, setToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
